import type { Component, JSX } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useI18n } from "~/i18n";
import { at } from "./parts";

/* The notch: Chief's black tab hanging from the menu bar over whatever app holds
   the call, dropping its cards down out of it. The desktop app draws it
   natively in services/chief/pill_darwin.mm, so nothing shared keeps these in
   step: redraw them when that changes. */

/**
 * The card the notch drops when it hears a call start, on Zoom here: which
 * app, the question, and Start Live Session. `pressAt` presses the button and `cursor`
 * lays a pointer over it.
 */
export const MeetingNotchCard: Component<{
  pressAt?: number;
  cursor?: JSX.Element;
  class?: string;
  style?: JSX.CSSProperties;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div
      class={`flex flex-col gap-1 rounded-b-[22px] bg-black px-4 pt-3 pb-3.5 text-white shadow-2xl ${props.class ?? ""}`}
      style={props.style}
    >
      <BrandGlyph src="/images/logos/zoom.svg" class="h-3 w-9 text-white/70" />
      <p class="text-sm font-semibold">{t("marketing.mock.notch.meeting_title")}</p>
      <p class="text-xs text-white/60">{t("marketing.mock.notch.meeting_body", { app: "Zoom" })}</p>
      <span
        class="relative mt-2 self-start"
        classList={{ press: props.pressAt !== undefined }}
        style={at(props.pressAt ?? 0)}
      >
        <span class="flex h-7 items-center rounded-lg bg-notch-button px-3 text-[13px] font-medium">
          {t("marketing.home.how.start_session")}
        </span>
        {props.cursor}
      </span>
    </div>
  );
};

/** The notch folded to its wings while a Live Session records: the red dot and the clock. */
export const NotchLive: Component<{ clock: string; class?: string; style?: JSX.CSSProperties }> = (props) => (
  <span
    class={`flex h-7 items-center gap-1.5 rounded-b-xl bg-black px-3.5 text-xs font-semibold text-white tabular-nums ${props.class ?? ""}`}
    style={props.style}
  >
    <span class="size-2 rounded-full bg-danger" />
    {props.clock}
  </span>
);

/**
 * A card Chief dropped out of the notch on its own: the kind and the line it
 * answers, the title, a short body, and Read full answer beside Dismiss.
 */
export const AnswerNotchCard: Component<{
  eyebrow: JSX.Element;
  quote: string;
  title: string;
  body: string;
  class?: string;
  style?: JSX.CSSProperties;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div
      class={`flex flex-col gap-1.5 rounded-b-[22px] bg-black px-4 pt-3 pb-3.5 text-white shadow-2xl ${props.class ?? ""}`}
      style={props.style}
    >
      <span class="flex items-center gap-1.5 text-[11px] font-semibold text-notch-accent">{props.eyebrow}</span>
      <p class="line-clamp-2 text-xs text-white/60 italic">“{props.quote}”</p>
      <p class="text-[15px] leading-snug font-semibold">{props.title}</p>
      <p class="line-clamp-3 text-xs leading-relaxed text-white/70">{props.body}</p>
      <span class="mt-1.5 flex items-center gap-2 text-[13px] font-medium">
        <span class="flex h-7 items-center rounded-lg bg-notch-button px-3">
          {t("marketing.mock.notch.read_answer")}
        </span>
        <span class="flex h-7 items-center rounded-lg bg-white/11 px-3">{t("marketing.mock.insights.dismiss")}</span>
      </span>
    </div>
  );
};
